import { ThemeEvents } from '@theme/events';

/**
 * Keeps the buy-box price surfaces in sync with Skio's one-time vs subscribe selection.
 *
 * Skio renders in light DOM and drives a hidden `input[name="selling_plan"]`:
 *   - empty value        → one-time selected
 *   - a selling-plan id  → subscribe selected (any frequency; all share the same % here)
 * It fires a bubbling `change` on that input whenever the shopper toggles plan or frequency.
 *
 * The price block server-renders BOTH states as siblings ([data-price-plan="one-time"|"subscribe"],
 * the subscribe one carrying `.hidden`); this component just toggles which is visible, and rewrites
 * the add-to-cart label price.
 *
 * On `variant:update` it does two things. The price block re-renders itself from the fetched document
 * (product-price.js), so the visible fragment has to be re-picked. The add-to-cart component does NOT —
 * a variant change morphs only <variant-picker> — so its price attributes are refreshed here from the
 * same document before the label is rewritten.
 */
class SubscriptionPriceSync extends HTMLElement {
  connectedCallback() {
    this.#apply();
    document.addEventListener('change', this.#onChange, true);
    const target = this.closest('.shopify-section, dialog') || document;
    target.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    this.#target = target;
  }

  disconnectedCallback() {
    document.removeEventListener('change', this.#onChange, true);
    this.#target?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  #target = null;

  get #isSubscribe() {
    const input = document.querySelector('input[name="selling_plan"]');
    return !!(input && input.value && input.value.trim() !== '');
  }

  #onChange = () => this.#apply();

  #onVariantUpdate = (event) => {
    // A PDP variant change morphs ONLY <variant-picker> — the add-to-cart price attributes are
    // server-rendered onto <add-to-cart-component> and nothing else updates them, so refresh them from
    // the incoming document first. Without this the button keeps advertising the previously selected
    // variant's price (e.g. still "$19.99" after switching from 100 to 40 loads), because #apply() just
    // re-writes the label from those same stale attributes.
    const html = event?.detail?.data?.html;
    if (html) this.#refreshPriceData(html);

    // The price fragments were re-rendered in their default (one-time) state; re-apply after paint.
    requestAnimationFrame(() => this.#apply());
  };

  /**
   * Copies the freshly server-rendered price attributes onto the live add-to-cart components.
   * @param {Document} html - The document fetched for the newly selected variant.
   */
  #refreshPriceData(html) {
    // Not filtered on [data-price-onetime]: a component that lost the attributes on a sold-out variant
    // still has to get them back when the shopper returns to a purchasable one.
    document.querySelectorAll('add-to-cart-component').forEach((component) => {
      // The component itself carries no id; its submit button does (BuyButtons-ProductSubmitButton-<block id>).
      // Quick-add buttons are rendered without an id, which conveniently scopes this to the buy box.
      const buttonId = component.querySelector('button[id]')?.id;
      if (!buttonId) return;

      const next = html.getElementById(buttonId)?.closest('add-to-cart-component');
      if (!next) return;

      // Liquid only emits the price attributes when the variant can be added to cart. When the new variant
      // can't be, drop them: product-form.js has already morphed the button to the server's sold-out label,
      // and leaving stale attributes in place would make #apply() overwrite that label with a price for a
      // variant nobody can buy.
      if (next.dataset.priceOnetime == null) {
        delete component.dataset.baseLabel;
        delete component.dataset.priceOnetime;
        delete component.dataset.priceSubscribe;
        return;
      }

      for (const key of ['baseLabel', 'priceOnetime', 'priceSubscribe']) {
        const value = next.dataset[key];
        if (value != null) component.dataset[key] = value;
      }
    });
  }

  #apply() {
    const subscribe = this.#isSubscribe;

    // Top/sticky price fragments: show the fragment matching the active plan.
    document.querySelectorAll('[data-price-plan]').forEach((el) => {
      const isSubscribeFragment = el.dataset.pricePlan === 'subscribe';
      el.classList.toggle('hidden', isSubscribeFragment !== subscribe);
    });

    // Add-to-cart label price (server renders the one-time price; swap to the subscribe price when active).
    document.querySelectorAll('add-to-cart-component[data-price-onetime]').forEach((component) => {
      const price = subscribe ? component.dataset.priceSubscribe : component.dataset.priceOnetime;
      if (!price) return;
      const base = component.dataset.baseLabel || '';
      const span = component.querySelector('.add-to-cart-text__content span span');
      if (span) span.textContent = base ? `${base} - ${price}` : price;
    });
  }
}

if (!customElements.get('subscription-price-sync')) {
  customElements.define('subscription-price-sync', SubscriptionPriceSync);
}
